import {endpointKeys} from '@common/api'
import {
  PartnerAdministrator,
  Partner,
  UpdatePartnerRequest,
  EmailTemplateResponse,
} from '@common/models'
import {
  partnerAdministratorNormaliser,
  partnerNormaliser,
  emailTemplateResponseNormaliser,
} from '@common/normalisers'

import {useWebGet, useApi, useWebSet, useWebVariableUrlSet} from './helpers'

/**
 * Hook to get the current partner administrator profile
 * Calls GET /api/v1/partner-administrators/me
 */
export const useGetCurrentPartnerAdministrator = () =>
  useWebGet<PartnerAdministrator>(
    [endpointKeys.partnerAdministrator],
    useApi().partnerAdministrator(),
    partnerAdministratorNormaliser,
  )

/**
 * Hook to get the partner information associated with the current partner administrator
 * Calls GET /api/v1/partner-administrators/partner
 */
export const useGetPartnerAdministratorPartner = () =>
  useWebGet<Partner>(
    [endpointKeys.partnerAdministratorPartner],
    useApi().partnerAdministratorPartner(),
    partnerNormaliser,
  )

/**
 * Hook to update partner information
 * Calls PUT /api/v1/partner-administrators/partner
 */
export const useUpdatePartnerAdministratorPartner = () =>
  useWebSet<UpdatePartnerRequest, Partner>(
    [endpointKeys.partnerAdministratorPartner],
    useApi().partnerAdministratorPartner(),
    'PUT',
    partnerNormaliser,
    [
      [endpointKeys.partnerAdministrator],
      [endpointKeys.partnerAdministratorPartner],
    ], // Invalidate related queries
  )

/**
 * Hook to get email template for a specific email type
 * Calls GET /api/v1/partner-administrators/emailTemplates/{emailType}
 */
export const useGetEmailTemplate = (emailType: string) =>
  useWebGet<EmailTemplateResponse>(
    [endpointKeys.partnerAdminEmailTemplate, emailType],
    useApi().partnerAdminEmailTemplate({emailType}),
    emailTemplateResponseNormaliser,
    {enabled: !!emailType},
  )

/**
 * Hook to update email template for a specific email type
 * Calls PUT /api/v1/partner-administrators/emailTemplates/{emailType}
 */
export const useUpdateEmailTemplate = (emailType: string) => {
  const api = useApi()
  return useWebVariableUrlSet<
    {emailType: string},
    {subject: string; section1: string; section2: string},
    EmailTemplateResponse
  >(
    [endpointKeys.partnerAdminEmailTemplate, emailType],
    ({emailType: et}) => api.partnerAdminEmailTemplate({emailType: et}),
    'PUT',
    emailTemplateResponseNormaliser,
    undefined, // options
    [[endpointKeys.partnerAdminEmailTemplate, emailType]], // invalidationKeys
  )
}

/**
 * Hook to send test email for a specific email type
 * Calls POST /api/v1/partner-administrators/emailTemplates/{emailType}/test
 */
export const useSendTestEmail = (emailType: string) => {
  const api = useApi()
  return useWebVariableUrlSet<
    {emailType: string},
    {recipientEmail: string},
    boolean
  >(
    [endpointKeys.partnerAdminEmailTemplateTest, emailType],
    ({emailType: et}) => api.partnerAdminEmailTemplateTest({emailType: et}),
    'POST',
    (data) => data as boolean,
    undefined, // options
    undefined, // invalidationKeys
  )
}
